import {
  Autocomplete,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import SkeletonWrapper from "../../../../common/components/functional/skeletonWrapper/SkeletonWrapper";
import { Certificate, TalentCertificate } from "../../../talents/talentAPI";
import Approval from "./Approval";
import WorkPermitDocuments from "./WorkPermitDocuments";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { leadingZerosDateFormat } from "../../../../common/helpers/formatDateTime";
import { CertificateItem } from "./types";
import isEmpty from "../../../../common/helpers/isEmpty";
import { VALID_FOR_FIXED_PERIOD_TYPES } from "../../constants";

type Props = {
  certificateTypeTitle: string;
  certificateList: CertificateItem[];
  certificate?: TalentCertificate;
  existingCertificateTypes: string[];
  onInputChange: (term: string) => void;
  handleRemoveExistingDocuments: (
    filename: string,
    id: string,
    version: number,
  ) => void;
  saveDisabled: boolean;
  isEditMode?: boolean;
  isPolling: boolean;
};

const WorkPermitForm = ({
  certificateTypeTitle,
  certificateList,
  certificate,
  existingCertificateTypes,
  onInputChange,
  handleRemoveExistingDocuments,
  saveDisabled,
  isEditMode,
  isPolling,
}: Props) => {
  const { t } = useTranslation(["common", "talentProfile"]);
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    resetField,
    formState: { defaultValues, errors },
  } = useFormContext();

  const [currentValidTo, setCurrentValidTo] = useState<string | null>(null);
  const watchedCertificateForm = useWatch();
  const isDocumentRequired = watchedCertificateForm.type?.requiresDocument;
  const certificateType = watchedCertificateForm.type?.id;
  // Set disabled value to local state because form resets in edit mode
  // when there are documents loading/polling - this prevents blinking.
  const [disableDocumentsAndApproval, setDisableDocumentsAndApproval] =
    useState<boolean>(true);
  const [disableValidUntilFurtherNotice, setDisableValidUntilFurtherNotice] =
    useState<boolean>(true);

  // Check disabled state per certificate type
  useEffect(() => {
    if (isDocumentRequired !== undefined) {
      setDisableDocumentsAndApproval(!isDocumentRequired);
      if (!isDocumentRequired) {
        // console.log("### resetField: documents");
        resetField("documents");
      }
    }
  }, [isDocumentRequired, resetField]);

  // Disable valid until further notice checkbox for certificate types that are only valid for a fixed period
  useEffect(() => {
    if (!certificateType) return;
    if (VALID_FOR_FIXED_PERIOD_TYPES.includes(certificateType as string)) {
      setDisableValidUntilFurtherNotice(true);
      setValue("validUntilFurtherNotice", false, { shouldDirty: false });
    } else {
      setDisableValidUntilFurtherNotice(false);
    }
  }, [certificateType, setValue]);

  const requiredFieldMessage = t("common:requiredField");

  const validUntilFurtherNotice =
    watchedCertificateForm.validUntilFurtherNotice;

  // required error is displayed only when the form has been edited and it could be sent
  useEffect(() => {
    if (!saveDisabled && !getValues("type")?.id) {
      setError("type", { type: "emptyField", message: requiredFieldMessage });
    }
  }, [saveDisabled, setError, getValues, requiredFieldMessage]);

  const handleChangeRejected = (checked: boolean) => {
    if (certificate?.approvedAt) return;

    const approved = watchedCertificateForm.approved;
    if (checked && approved) {
      setValue("approved", false, { shouldDirty: true });
    }
  };

  const handleChangeApproved = (checked: boolean) => {
    if (certificate?.rejectedAt) return;

    const rejected = watchedCertificateForm.rejected;
    if (checked && rejected) {
      setValue("rejected", false, { shouldDirty: true });
    }
  };

  const requiredDocumentIsMissing =
    isDocumentRequired && isEmpty(getValues("documents"));

  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 50);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 50);

  const checkboxesAreDisabled =
    isPolling ||
    requiredDocumentIsMissing ||
    watchedCertificateForm.type === null;
  certificateList.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Grid container alignItems="center" columnSpacing={2} rowSpacing={1}>
      <Grid item textAlign="end" xs={4}>
        <Typography color="textSecondary">{certificateTypeTitle}</Typography>
      </Grid>
      <Grid item xs={8}>
        <Controller
          name="type"
          control={control}
          rules={{
            required: { value: true, message: requiredFieldMessage },
          }}
          render={({ field: { onChange, ...rest } }) => (
            <Autocomplete
              {...rest}
              options={certificateList}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionDisabled={(option) =>
                existingCertificateTypes.includes(option.id)
              }
              disabled={isEditMode}
              onChange={(_, newValue: Certificate | null) => {
                onChange(newValue);
                if (newValue?.id) {
                  clearErrors("type");
                }
              }}
              onInputChange={(_, newValue) => onInputChange(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  required
                  error={!!errors.type}
                  helperText={errors.type?.message as string}
                />
              )}
            />
          )}
        />
      </Grid>

      {!disableDocumentsAndApproval && (
        <Grid item textAlign="end" alignSelf="flex-start" xs={4}>
          <Typography color="textSecondary">
            {t("talentProfile:document")}
          </Typography>
        </Grid>
      )}
      <WorkPermitDocuments
        name="documents"
        certificate={certificate}
        control={control}
        handleRemoveExistingDocuments={handleRemoveExistingDocuments}
        showUploadButton={!disableDocumentsAndApproval}
      />
      <Grid item textAlign="end" xs={4}>
        <Typography color="textSecondary">
          {t("talentProfile:validTo")}
        </Typography>
      </Grid>
      <Grid item xs={8}>
        <SkeletonWrapper
          value={!validUntilFurtherNotice}
          height="40px"
          width="135px"
          sx={{ margin: 0 }}
        >
          <Controller
            name="validTo"
            control={control}
            rules={{
              required: {
                value: !validUntilFurtherNotice,
                message: requiredFieldMessage,
              },
              validate: (value) => {
                return (
                  (value instanceof Date && !isNaN(value.getTime())) ||
                  t("common:invalidDate")
                );
              },
            }}
            render={({ field: { onBlur, value, ...rest } }) => (
              <DesktopDatePicker
                {...rest}
                disabled={isPolling}
                value={value}
                minDate={minDate}
                maxDate={maxDate}
                format={leadingZerosDateFormat}
                slotProps={{
                  textField: {
                    size: "small",
                    error: !!errors.validTo,
                    helperText: errors.validTo?.message as string,
                    sx: {
                      width: "135px",
                    },
                  },
                }}
              />
            )}
          />
        </SkeletonWrapper>
      </Grid>
      {!disableValidUntilFurtherNotice && (
        <>
          <Grid item xs={4} />
          <Grid item xs={8}>
            <Controller
              name="validUntilFurtherNotice"
              control={control}
              render={({ field: { value, onChange, ...rest } }) => (
                <FormControlLabel
                  label={t("talentProfile:validUFN")}
                  control={
                    <Checkbox
                      {...rest}
                      disabled={isPolling}
                      checked={validUntilFurtherNotice}
                      onChange={(_, checked) => {
                        if (checked) {
                          const curr =
                            getValues("validTo") ??
                            defaultValues?.validTo ??
                            "";
                          setCurrentValidTo(curr);
                          setValue("validTo", "", { shouldValidate: false });
                        } else {
                          setValue("validTo", currentValidTo, {
                            shouldValidate: true,
                          });
                        }
                        setValue("validUntilFurtherNotice", checked);
                      }}
                    />
                  }
                />
              )}
            />
          </Grid>
        </>
      )}
      {!disableDocumentsAndApproval && (
        <>
          <Divider sx={{ width: "100%", marginTop: "8px" }} />
          <Grid item textAlign="end" xs={4} />
          <Grid item xs={8}>
            <Controller
              name="approved"
              control={control}
              render={({ field: { value, onChange, ...rest } }) =>
                !certificate?.approvedBy && !certificate?.approvedAt ? (
                  <FormControlLabel
                    label={t("common:approve")}
                    control={
                      <Checkbox
                        disabled={checkboxesAreDisabled}
                        checked={!!value}
                        onChange={(_, checked) => {
                          onChange(checked);
                          handleChangeApproved(checked);
                        }}
                        {...rest}
                      />
                    }
                  />
                ) : (
                  <Approval
                    approval="approved"
                    date={certificate?.approvedAt}
                    expert={certificate?.approvedBy}
                  />
                )
              }
            />
            <Grid item xs={8}>
              <Controller
                name="rejected"
                control={control}
                render={({ field: { value, onChange, ...rest } }) =>
                  !certificate?.rejectedBy && !certificate?.rejectedAt ? (
                    <FormControlLabel
                      label={t("common:reject")}
                      control={
                        <Checkbox
                          disabled={checkboxesAreDisabled}
                          checked={!!value}
                          onChange={(_, checked) => {
                            onChange(checked);
                            handleChangeRejected(checked);
                          }}
                          {...rest}
                        />
                      }
                    />
                  ) : (
                    <Approval
                      approval="rejected"
                      date={certificate?.rejectedAt}
                      expert={certificate?.rejectedBy}
                    />
                  )
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default WorkPermitForm;
